import { buildConnection } from '$app/arrp-hub-connection-builder'
import apiConnection from '$app/plugins/api-connection'
import defaultPagingInfo from '$app/default-paging-info'
const connection = buildConnection('Provider');

// Error Codes
const errCodes = {
  'has-enrollments': 'Provider has linked enrollment records.\nDelete or move enrollments before deleting provider.',
  'has-rates': 'Unable To Delete Provider.\nReadiness Rates have already been calculated.',
  'duplicate-provider': `Unable to save duplicate provider. The supplied values for 'Provider ID' and 'Program Type' already exist in the database.`
};

const getDelErr = errCode => errCodes[errCode] || 'Unable To Delete Provider';
const getSaveErr = errCode => errCodes[errCode] || 'Unable To Save Provider';

// STATE
const state = {
  loading: false,
  nextId: 0,
  pagingInfo: defaultPagingInfo(),
  items: [],
  locks: [],
  totalItems: 0,
  searchModel: {
    providerNameOrId: '',
    countyIds: [],
    coalitionIds: [],
    reduceToErrors: false,
    reduceToWarnings: false
  },
  isPrelimReadinessRateCalcPermitted: false,
  isFinalReadinessRateCalcPermitted: false
}

// MUTATIONS
const mutations = {
  updateSearchModel(state, searchModel) {
    state.searchModel = searchModel;
    state.pagingInfo.page = 1;
  },
  updateTotalItems(state, totalItems) {
    state.totalItems = totalItems;
  },
  addItem(state, item) {
    state.items.push(item);
    state.nextId = item.id + 1;
  },
  setLocks(state, locks) {
    state.locks = locks;
  },
  updateItems(state, items) {
    state.items = [...items];
  },
  updatePagingInfo(state, pagingInfo) {
    state.pagingInfo = { ...pagingInfo };
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setRateCalcsPermitted(state, permitted) {
    state.isPrelimReadinessRateCalcPermitted = permitted.prelim;
    state.isFinalReadinessRateCalcPermitted = permitted.final;
  }
}

// ACTIONS
const actions = ({
  async connect({ commit, dispatch }) {
    connection.onLocksUpdated((locks) => commit('setLocks', locks));
    connection.onEntitiesUpdated(() => dispatch('search'));

    await connection.connect();
  },
  async disconnect() {
    await connection.disconnect();
  },
  lockItem({ commit }, item) {
    connection.lockById(item.pcavId);
  },
  unlockItem({ commit }, item) {
    connection.unlockById(item.pcavId);
  },
  search({ commit, state }) {
    const searchModel = { ...state.searchModel, pagingInfo: state.pagingInfo };
    commit('setLoading', true);
    apiConnection.post('api/arrp/SearchProviders', searchModel).then(resp => {
      const items = resp.data.items;

      commit('updateItems', items);
      commit('updateTotalItems', resp.data.totalItems);
    }).finally(() => commit('setLoading', false));
  },
  saveOrAddItem({ dispatch }, item) {
    const post = () => apiConnection.post('api/arrp/saveProvider', { ...item })
      .then((postResp) => {
        if (postResp && postResp.data) {
          if (postResp.data.success) {
            dispatch('search');
          } else {
            this._vm.$snackbar(getSaveErr(postResp.data.errorMessage), 'error');
          }
        } else {
          this._vm.$snackbar('Unable to Save Provider', 'error');
        }
      })
      .catch(() => this._vm.$snackbar('Unable to Save Provider', 'error'));

    apiConnection.get(`api/arrp/providerExists?providerId=${item.providerId}`)
      .then((getResp) => {
        const exists = getResp && getResp.data && getResp.data.exists === true;
        if (exists) {
          post();
        } else {
          const text = `The provider ID '${item.providerId}' does not currently exist in the system. Continuing will create a new provider login which will require registration. Do you wish to create Provider ${item.providerId}?`;
          this._vm.$confirm({ title: 'Create New Account?', text, yesLabel: 'Create' })
            .then((conf) => {
              if (conf) {
                post();
              }
            });
        }
      });
  },
  itemAdded({ dispatch }, item) {
    dispatch('saveOrAddItem', item);
  },
  itemEdited({ dispatch }, item) {
    dispatch('saveOrAddItem', item);
  },
  deleteItem({ dispatch }, item) {
    return new Promise((resolve, reject) => {
      apiConnection.delete(`api/arrp/deleteProvider?pcavId=${item.pcavId}`)
        .then(resp => {
          if (resp && resp.data) {
            if (resp.data.success) {
              resolve(resp.data);
            } else {
              reject(getDelErr(resp.data.errorMessage));
            }
          } else {
            reject(new Error('Unable To Delete Provider'))
          }
        })
        .catch((err) => {
          if (err.response && err.response.data) {
            reject(new Error(getDelErr(err.response.data.errorMessage)));
          } else {
            reject(new Error('Unable To Delete Provider'));
          }
        });
    });
  },
  sortItems({ commit, state, dispatch }, sortValues) {
    const pagingInfo = { ...state.pagingInfo, page: 1, ...sortValues };
    commit('updatePagingInfo', pagingInfo);
    dispatch('search');
  },
  pagingInfoUpdated({ commit }, pagingInfo) {
    commit('updatePagingInfo', pagingInfo);
  },
  getPagedData({ commit, state, dispatch }, page) {
    const pagingInfo = { ...state.pagingInfo, page };
    commit('updatePagingInfo', pagingInfo);
    dispatch('search');
  },
  areRateCalcsPermitted({ commit }) {
    apiConnection.get('api/arrp/AreRateCalcsPermitted')
      .then(resp => {
        commit('setRateCalcsPermitted', resp.data)
      });
  }
})

// GETTERS
const getters = {
  searchModel: state => state.searchModel,
  currentPage: state => state.items.map(item => ({ ...item, locked: state.locks.includes(item.pcavId) })),
  isPrelimReadinessRateCalcPermitted: state => state.isPrelimReadinessRateCalcPermitted,
  isFinalReadinessRateCalcPermitted: state => state.isFinalReadinessRateCalcPermitted
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
